import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _values from "lodash/values";
import _some from "lodash/some";
import _toLower from "lodash/toLower";
import _isEmpty from "lodash/isEmpty";
import _isNil from "lodash/isNil";
import "core-js/modules/es6.array.filter.js";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import classes from '@nerdwallet/base-styles/classes';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { Tooltip, Accent } from '@nerdwallet/currency';
import { FORMATS, VALUE_SIZES } from './constants';
import QuickFactValue from './quick-fact-value';
import styles from "./quick-facts.module.css";

var QuickFacts = function QuickFacts(_ref) {
  var analytics = _ref.analytics,
      className = _ref.className,
      enableAnalytics = _ref.enableAnalytics,
      quickFacts = _ref.quickFacts,
      groupLabelAndValue = _ref.groupLabelAndValue;
  useDeprecatedPropsWarning({
    component: 'QuickFacts',
    deprecatedProps: [{
      version: '5.5.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics instead.'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });
  var quickFactComponents = [];
  var filteredQuickFacts = quickFacts.filter(function (quickFact) {
    return quickFact && !_isNil(quickFact.value) || groupLabelAndValue;
  });

  if (_isEmpty(filteredQuickFacts)) {
    return null;
  }

  var rewardsDrivers = filteredQuickFacts.some(function (quickFact) {
    return _toLower(quickFact.label).indexOf('rewards') >= 0;
  }); // Need to use a forEach here because styling will not work if each
  // quickFact is wrapped in an enclosing div. All items must be
  // direct siblings.

  filteredQuickFacts.forEach(function (quickFact) {
    var _ref2;

    var labelNode = /*#__PURE__*/React.createElement("div", {
      key: "".concat(quickFact.id, "-label"),
      className: classNames([styles.cell, styles.label, classes('padding-bottom-2')])
    }, quickFact.labelTooltip ? /*#__PURE__*/React.createElement(Tooltip, {
      id: quickFact.id,
      label: /*#__PURE__*/React.createElement(Accent, {
        size: "small"
      }, quickFact.label),
      content: quickFact.labelTooltip,
      title: "More information about ".concat(quickFact.label),
      analytics: (enableAnalytics || analytics) && _objectSpread({
        entityNamePrefix: "quick_facts_component_tooltip"
      }, analytics)
    }) : /*#__PURE__*/React.createElement(Accent, {
      size: "small"
    }, quickFact.label));
    var valueNode = /*#__PURE__*/React.createElement("div", {
      key: "".concat(quickFact.id, "-value"),
      className: classNames([styles.cell, styles.value])
    }, /*#__PURE__*/React.createElement(QuickFactValue, {
      analytics: analytics,
      quickFact: quickFact,
      showNAForEmptyValue: groupLabelAndValue
    }));
    var fullWidthDriver = _toLower(quickFact.label).indexOf('regular apr') >= 0;
    if (groupLabelAndValue) quickFactComponents.push( /*#__PURE__*/React.createElement("div", {
      className: classNames([styles.driverColumn, (_ref2 = {}, _defineProperty(_ref2, styles.centeredDriver, rewardsDrivers), _defineProperty(_ref2, styles.fullWidthDriver, fullWidthDriver), _ref2)])
    }, labelNode, valueNode));else {
      quickFactComponents.push(labelNode);
      quickFactComponents.push(valueNode);
    }
  });

  var hasWideDriver = _some(filteredQuickFacts, 'wide');

  return /*#__PURE__*/React.createElement("div", {
    className: classNames([className, groupLabelAndValue ? styles.quickFactsFlex : styles.quickFactsTable, styles["table".concat(filteredQuickFacts.length, "Cols")]], _defineProperty({}, styles.hasWideDriver, hasWideDriver))
  }, quickFactComponents);
};

QuickFacts.propTypes = {
  /** Class name(s) to apply to the component */
  className: PropTypes.string,

  /**
   * Flag to enable bedrock analytics
   */
  enableAnalytics: PropTypes.oneOfType([PropTypes.bool]),

  /** Analytics Prop */
  analytics: PropTypes.shape({
    entityName: PropTypes.string
  }),

  /** List of quick facts to display */
  quickFacts: PropTypes.arrayOf(PropTypes.shape({
    /** The unique id for this particular quick fact */
    id: PropTypes.string.isRequired,

    /** The quick fact label text */
    label: PropTypes.node,

    /** The popover text to be associated with the label */
    labelTooltip: PropTypes.node,

    /** The quick fact value */
    value: PropTypes.node,

    /** Specifies how the value should be formatted.
    Supports 'currency', 'currencyWithCents', 'percent', 'string', and 'unformatted'.
    Defaults to 'string'. */
    valueFormat: PropTypes.oneOf(_values(FORMATS)),

    /** The popover text to be associated with the value */
    valueTooltip: PropTypes.node,

    /** The type size of the value. Supports 'large' and 'small'.
     Defaults to 'large'. */
    valueSize: PropTypes.oneOf(_values(VALUE_SIZES)),

    /** The quick fact description */
    description: PropTypes.node,

    /** Whether to display the driver in wider width, e.g. the LOA driver */
    wide: PropTypes.bool
  })).isRequired,
  groupLabelAndValue: PropTypes.bool
};
QuickFacts.defaultProps = {
  analytics: {},
  className: '',
  groupLabelAndValue: false
};
export default QuickFacts;